// src/App.tsx
import React from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import Login from "./pages/Login";
import StudentDashboard from "./pages/StudentDashboard";
import FacultyDashboard from "./pages/FacultyDashboard";
import StudentDetail from "./pages/StudentDetail";
import CreateCohort from "./pages/CreateCohort";
import CohortAnalytics from "./pages/CohortAnalytics";
import StudentCohorts from "./pages/StudentCohorts";
import { auth } from "./firebase";
import { onAuthStateChanged } from "firebase/auth";
import SchedulerStudent from "./pages/SchedulerStudent";
import SchedulerAdmin from "./pages/SchedulerAdmin";

/** THEME INIT (keeps routes/logic untouched) */
function initTheme() {
  const saved = (localStorage.getItem("theme") as "light" | "dark") || "dark";
  if (typeof document !== "undefined") {
    document.body.classList.toggle("light-theme", saved === "light");
  }
}

function RequireAuth({ children }: { children: JSX.Element }) {
  const [ready, setReady] = React.useState(false);
  const [authed, setAuthed] = React.useState(false);
  const loc = useLocation();

  React.useEffect(() => {
    initTheme(); // apply theme once on any protected page
    const unsub = onAuthStateChanged(auth, (user) => {
      setAuthed(!!user);
      setReady(true);
    });
    return () => unsub();
  }, []);

  if (!ready) return <p style={{ padding: 16 }}>Loading…</p>;
  if (!authed) return <Navigate to="/login" state={{ from: loc }} replace />;
  return children;
}

export default function App() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />

      {/* Student area */}
      <Route
        path="/s"
        element={
          <RequireAuth>
            <StudentDashboard />
          </RequireAuth>
        }
      />
      <Route
        path="/s/cohorts"
        element={
          <RequireAuth>
            <StudentCohorts />
          </RequireAuth>
        }
      />
      <Route
        path="/s/scheduler"
        element={
          <RequireAuth>
            <SchedulerStudent />
          </RequireAuth>
        }
      />

      {/* Faculty area */}
      <Route
        path="/f"
        element={
          <RequireAuth>
            <FacultyDashboard />
          </RequireAuth>
        }
      />
      <Route
        path="/f/groups"
        element={
          <RequireAuth>
            <CreateCohort />
          </RequireAuth>
        }
      />
      <Route
        path="/f/groups/:groupId"
        element={
          <RequireAuth>
            <CohortAnalytics />
          </RequireAuth>
        }
      />
      <Route
        path="/f/scheduler"
        element={
          <RequireAuth>
            <SchedulerAdmin />
          </RequireAuth>
        }
      />

      {/* Shared */}
      <Route
        path="/student/:uid"
        element={
          <RequireAuth>
            <StudentDetail />
          </RequireAuth>
        }
      />

      <Route path="*" element={<Navigate to="/login" replace />} />
    </Routes>
  );
}
